import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Download, LogOut, Search, Loader2, ArrowLeft } from "lucide-react";
import { Crown } from "@/components/brand";
import { STATES, API } from "@/constants/states";

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const STATUS_STYLES = {
  new: "bg-[#C9A24B]/15 text-[#E8C87A] border-[#C9A24B]/40",
  contacted: "bg-[#0E7C86]/20 text-[#5FC3CC] border-[#0E7C86]/50",
  dispatched: "bg-[#10B981]/15 text-[#34D399] border-[#10B981]/40",
};

const inputCls =
  "bg-[#092237] border border-[#C9A24B]/25 text-[#FBF8F2] text-sm px-4 py-2.5 rounded-lg placeholder-[#8EA3B0]/60 focus:outline-none focus:border-[#C9A24B]";

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [metrics, setMetrics] = useState({ total: 0, new: 0, contacted: 0, dispatched: 0 });
  const [filters, setFilters] = useState({ q: "", state: "", status: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.state) params.state = filters.state;
      if (filters.status) params.status = filters.status;
      const [listRes, metricsRes] = await Promise.all([
        axios.get(`${API}/referrals`, { params, withCredentials: true }),
        axios.get(`${API}/referrals/metrics`, { withCredentials: true }),
      ]);
      setReferrals(listRes.data);
      setMetrics(metricsRes.data);
    } catch {
      toast.error("Could not load referrals.");
    }
  }, [filters]);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success("Welcome back, Queen.");
    } catch (err) {
      setLoginError(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/referrals/${id}`, { status }, { withCredentials: true });
      setReferrals((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/referrals/export/csv`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "collision-queens-referrals.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#04111D] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24B] animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#04111D] flex items-center justify-center px-4">
        <div className="grain-overlay" />
        <div data-testid="admin-login-dialog" className="w-full max-w-md bg-[#071A2B] border border-[#C9A24B]/25 p-10 shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
          <div className="flex flex-col items-center text-center">
            <Crown className="w-12 h-9 crown-glow" />
            <h1 className="mt-5 font-display font-bold text-xl text-[#FBF8F2] tracking-wider">REFERRAL COMMAND CENTER</h1>
            <p className="mt-2 font-data text-[10px] tracking-[0.28em] uppercase text-[#0E7C86]">Collision Queens Connect</p>
          </div>
          <form onSubmit={login} className="mt-8 flex flex-col gap-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              placeholder="Admin email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
            />
            <input
              data-testid="admin-passcode-input"
              type="password"
              required
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-[#EF4444]">{loginError}</p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={busy}
              className="mt-2 rounded-full bg-[#C9A24B] text-[#04111D] text-xs uppercase tracking-[0.16em] font-bold px-6 py-3.5 hover:bg-[#E8C87A] disabled:opacity-60 transition-all duration-300"
            >
              {busy ? "Verifying..." : "Enter Command Center"}
            </button>
          </form>
          <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-xs text-[#8EA3B0] hover:text-[#E8C87A] transition-colors duration-300">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-[#04111D] text-[#D4DFE6]">
      <div className="grain-overlay" />
      <header className="border-b border-[#C9A24B]/15 bg-[#071A2B]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-6 crown-glow" />
            <div>
              <p className="font-display font-bold text-sm tracking-[0.14em] text-[#FBF8F2]">REFERRAL COMMAND CENTER</p>
              <p className="font-data text-[9px] tracking-[0.28em] uppercase text-[#0E7C86]">Collision Queens Connect</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-[#8EA3B0] hover:text-[#E8C87A] transition-colors duration-300 hidden sm:block">
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold text-[#D4DFE6] border border-[#C9A24B]/30 rounded-full px-4 py-2 hover:border-[#C9A24B] hover:text-[#E8C87A] transition-all duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Referrals", value: metrics.total, testId: "admin-metric-total" },
            { label: "Pending Review", value: metrics.new, testId: "admin-metric-pending" },
            { label: "Contacted", value: metrics.contacted, testId: "admin-metric-contacted" },
            { label: "Partner Dispatched", value: metrics.dispatched, testId: "admin-metric-dispatched" },
          ].map((m) => (
            <div key={m.label} data-testid={m.testId} className="bg-[#092237] border border-[#C9A24B]/20 p-6">
              <p className="font-display font-black text-3xl text-[#E8C87A]">{m.value}</p>
              <p className="mt-1 font-data text-[10px] tracking-[0.2em] uppercase text-[#8EA3B0]">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8EA3B0]" />
            <input
              data-testid="admin-search-input"
              placeholder="Search name, email, or phone..."
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className={`${inputCls} w-full pl-10`}
            />
          </div>
          <select
            data-testid="admin-filter-state"
            value={filters.state}
            onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
            className={inputCls}
          >
            <option value="">All States</option>
            {STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            data-testid="admin-filter-status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Statuses</option>
            <option value="new">Pending Review</option>
            <option value="contacted">Contacted</option>
            <option value="dispatched">Partner Dispatched</option>
          </select>
          <button
            data-testid="admin-export-button"
            onClick={exportCsv}
            className="flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#04111D] text-xs uppercase tracking-[0.14em] font-bold px-5 py-2.5 hover:bg-[#E8C87A] transition-all duration-300"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="mt-6 overflow-x-auto border border-[#C9A24B]/20">
          <table data-testid="admin-referrals-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-[#071A2B] text-left">
                {["Name", "Contact", "State", "Services", "Submitted", "Notes", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3.5 font-data text-[10px] tracking-[0.2em] uppercase text-[#C9A24B] font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-[#8EA3B0] text-sm">
                    No referrals match these filters yet.
                  </td>
                </tr>
              )}
              {referrals.map((r) => (
                <tr key={r.id} data-testid={`admin-referral-row-${r.id}`} className="border-t border-[#C9A24B]/10 hover:bg-[#092237]/70 transition-colors duration-300">
                  <td className="px-4 py-4 font-semibold text-[#FBF8F2]">{r.name}</td>
                  <td className="px-4 py-4">
                    <a href={`tel:${r.phone}`} className="block text-[#5FC3CC] hover:underline">{r.phone}</a>
                    <a href={`mailto:${r.email}`} className="block text-xs text-[#8EA3B0] hover:text-[#E8C87A]">{r.email}</a>
                  </td>
                  <td className="px-4 py-4">{r.state}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(r.services || []).map((s) => (
                        <span key={s} className="text-[10px] rounded-full border border-[#0E7C86]/40 text-[#5FC3CC] px-2 py-0.5">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-xs text-[#8EA3B0] whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-4 text-xs text-[#8EA3B0] max-w-[220px] truncate" title={r.notes || ""}>
                    {r.notes || "—"}
                  </td>
                  <td className="px-4 py-4">
                    <select
                      data-testid={`admin-status-select-${r.id}`}
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`text-xs rounded-full border px-3 py-1.5 bg-[#04111D] focus:outline-none ${STATUS_STYLES[r.status] || ""}`}
                    >
                      <option value="new">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="dispatched">Dispatched</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
